import { useEffect, useState } from "react";

function escAttr(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function nombreDescarga(titulo) {
  const base = String(titulo || "documento")
    .replace(/[^\w.\- áéíóúüñÁÉÍÓÚÜÑ]+/g, "_")
    .trim()
    .slice(0, 80) || "documento";
  return /\.(jpe?g|png|webp|gif)$/i.test(base) ? base : `${base}.jpg`;
}

/** Imprime la imagen con la rotación actual (el visor PDF ya lo hace solo). */
export function imprimirImagenDocumento(src, rotacion, titulo) {
  const w = window.open("", "_blank");
  if (!w) {
    window.alert("Permite ventanas emergentes para imprimir.");
    return;
  }
  const deg = ((Number(rotacion) % 360) + 360) % 360;
  const girada = deg === 90 || deg === 270;
  w.document.open();
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escAttr(titulo || "Documento")}</title>
<style>
  @page { margin: 8mm; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { min-height: 100vh; display: flex; align-items: center; justify-content: center; }
  img {
    object-fit: contain;
    transform: rotate(${deg}deg);
    max-width: ${girada ? "100vh" : "100%"};
    max-height: ${girada ? "100vw" : "100vh"};
  }
</style></head><body>
<img alt="" src="${escAttr(src)}" onload="setTimeout(function(){window.focus();window.print();},250)" />
</body></html>`);
  w.document.close();
}

async function descargarImagenDocumento(src, titulo) {
  const nombre = nombreDescarga(titulo);
  try {
    const res = await fetch(src, { mode: "cors" });
    if (!res.ok) throw new Error("fetch");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2500);
  } catch {
    window.open(src, "_blank", "noopener,noreferrer");
  }
}

const btnStyle = {
  padding: "4px 8px",
  borderRadius: 6,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1.2,
};

/** Misma barra que el visor PDF: voltear, zoom, imprimir, descargar. */
export function BarraAccionesImagen({ src, title = "Documento", rot = 0, setRot, zoom = 1, setZoom }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end", flexShrink: 0 }}>
      <button type="button" title="Voltear a la izquierda" aria-label="Voltear a la izquierda" style={btnStyle}
        onClick={() => setRot((r) => (r + 270) % 360)}>↺</button>
      <button type="button" title="Voltear a la derecha" aria-label="Voltear a la derecha" style={btnStyle}
        onClick={() => setRot((r) => (r + 90) % 360)}>↻</button>
      <button type="button" title="Alejar" aria-label="Alejar" style={btnStyle}
        onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))}>−</button>
      <button type="button" title="Acercar" aria-label="Acercar" style={btnStyle}
        onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))}>+</button>
      <button type="button" title="Imprimir" aria-label="Imprimir" style={{ ...btnStyle, background: "#0f172a", color: "#fff", borderColor: "#0f172a" }}
        onClick={() => imprimirImagenDocumento(src, rot, title)}>Imprimir</button>
      <button type="button" title="Descargar" aria-label="Descargar" style={btnStyle}
        onClick={() => descargarImagenDocumento(src, title)}>Descargar</button>
    </div>
  );
}

/**
 * Vista de JPG/PNG con las acciones que el visor PDF ya trae.
 * hideToolbar: la barra va fuera (cabecera de Vista previa).
 */
export default function VisorImagenDocumento({
  src,
  title = "Documento",
  hideToolbar = false,
  rot: rotProp,
  setRot: setRotProp,
  zoom: zoomProp,
  setZoom: setZoomProp,
}) {
  const [rotI, setRotI] = useState(0);
  const [zoomI, setZoomI] = useState(1);
  const controlado = typeof setRotProp === "function";
  const rot = controlado ? rotProp : rotI;
  const setRot = controlado ? setRotProp : setRotI;
  const zoom = controlado ? zoomProp : zoomI;
  const setZoom = controlado ? setZoomProp : setZoomI;

  useEffect(() => {
    if (controlado) return;
    setRotI(0);
    setZoomI(1);
  }, [src, controlado]);

  const girada = ((rot || 0) % 180) !== 0;

  return (
    <div style={{
      width: "100%",
      height: "100%",
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}>
      {!hideToolbar && (
        <BarraAccionesImagen src={src} title={title} rot={rot} setRot={setRot} zoom={zoom} setZoom={setZoom} />
      )}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}>
        <img
          src={src}
          alt={title}
          style={{
            maxWidth: girada ? "none" : "100%",
            maxHeight: girada ? "none" : "100%",
            width: girada ? "auto" : undefined,
            height: girada ? "min(72vh, 640px)" : "auto",
            objectFit: "contain",
            transform: `rotate(${rot}deg) scale(${zoom})`,
            transformOrigin: "center center",
            borderRadius: 6,
          }}
        />
      </div>
    </div>
  );
}
